import '@fontsource/inter/latin-300.css';
import '@fontsource/inter/latin-400.css';
import '@fontsource/inter/latin-500.css';
import '@fontsource/inter/latin-600.css';
import '@fontsource/inter/latin-700.css';
import '@fontsource/jetbrains-mono/latin-400.css';
import '@fontsource/jetbrains-mono/latin-500.css';
import '@fontsource/jetbrains-mono/latin-600.css';
import '@fontsource/jetbrains-mono/latin-700.css';
import './styles/main.scss';
import { copyToClipboard, createOpenInMenu, resultText } from './utils/ui.js';
import { track } from './utils/analytics.js';
import { fireActivate, fireDeactivate, setNavigator, consumePrefill, registerOpenInTargets } from './panel.js';
import {
  autoSave,
  autoRestore,
  rememberDefaults,
  trackRecent,
  consumePendingRestore,
  setPendingRestore,
  captureShareState,
  upgradeState,
  initSessions,
  closeSessions,
} from './sessions.js';
import { encodeShareFragment, decodeShareFragment, isShareFragment, MAX_SHARE_URL_LENGTH } from './utils/share.js';
import { captureToWorkspace } from './workspace.js';
import { pinToNotebook } from './utils/notebook-store.js';
import { initNotebook, closeNotebook, refreshNotebook } from './notebook.js';
import { applyTextToPrimaryInput } from './utils/input-targets.js';
import {
  TOOLS,
  toolById,
  groupById,
  roomById,
  roomOfTool,
  toolsInRoom,
  SEO_DESCRIPTIONS,
  TOOL_RELEVANCE,
  TOOL_ALIASES,
  resolveToolId,
  pageById,
} from './registry.js';
import { initNavigation, setActiveTool, setActiveSide, renderToolHead, closeDrawer } from './navigation.js';
import { initPortal } from './portal.js';

// The tool registry (rooms, groups, tools, pages, SEO, relevance) lives in registry.js.
// This module owns the views — the portal (front page), a tool inside a side and a
// page such as Sources & method — and the theme.

// ─── SEO metadata ───────────────────────────────────────────────────────────

const DEFAULT_DESCRIPTION = `Decode hidden structure across language, symbols, time, number, and esoteric systems — ${TOOLS.length} free online tools. No signup required.`;
const DEFAULT_TITLE = 'Unveil — Nothing Stays Hidden';
const SITE_ORIGIN = 'https://unveil.tools';

function setMeta(title, desc, path) {
  const url = `${SITE_ORIGIN}${path}`;
  document.title = title;
  document.querySelector('meta[name="description"]')?.setAttribute('content', desc);
  document.querySelector('meta[property="og:title"]')?.setAttribute('content', title);
  document.querySelector('meta[property="og:description"]')?.setAttribute('content', desc);
  document.querySelector('meta[property="og:url"]')?.setAttribute('content', url);
  document.querySelector('meta[name="twitter:title"]')?.setAttribute('content', title);
  document.querySelector('meta[name="twitter:description"]')?.setAttribute('content', desc);
  document.querySelector('link[rel="canonical"]')?.setAttribute('href', url);
}

function updateMeta(toolId) {
  const tool = toolById[toolId];
  if (!tool) return;
  setMeta(`${tool.label} — Unveil`, SEO_DESCRIPTIONS[toolId] || DEFAULT_DESCRIPTION, toolPath(toolId));
}

function resetMeta() {
  setMeta(DEFAULT_TITLE, DEFAULT_DESCRIPTION, '/');
}

// ─── Cross-tool "Open in..." target registry ────────────────────────────────
// Tools with textInput: true accept text and can be targeted by "Open in..." actions.

registerOpenInTargets(
  TOOLS.filter((t) => t.textInput).map((t) => ({
    id: t.id,
    label: t.label,
    category: t.group,
    categoryLabel: `${roomById[groupById[t.group].room].label} / ${groupById[t.group].label}`,
  })),
  TOOL_RELEVANCE,
);

setupToast();

// Panel map — populated lazily as tools are first visited
const panelMap = new Map();
const panelsContainer = document.getElementById('panels-container');
const pageEl = document.getElementById('page');
const appEl = document.querySelector('.app');

// Track which tools have already been initialised
const initializedTools = new Set();

let activeTool = null;

// ─── Copy buttons (scoped to a single panel) ─────────────────────────────────

function setupCopyButtonsForPanel(panel) {
  const toolId = panel.dataset.toolPanel;

  panel.querySelectorAll('.result-card').forEach((card) => {
    const header = card.querySelector('.result-header');
    const output = card.querySelector('.tool-output, .rune-output');
    if (!header || !output) return;
    // A card marked data-secret (a generated password) gets Copy only: Open in, Clip and Pin
    // would put the secret into fields and stores that are saved
    const secret = card.hasAttribute('data-secret');

    let actions = header.querySelector('.result-actions');
    if (!actions) {
      actions = document.createElement('div');
      actions.className = 'result-actions';
      header.appendChild(actions);
    }

    const existingOpenIn = card.querySelector('.open-in');
    if (existingOpenIn && existingOpenIn.parentElement !== actions) {
      const openInWrapper = existingOpenIn.parentElement;
      actions.appendChild(existingOpenIn);
      if (
        openInWrapper &&
        openInWrapper !== actions &&
        !openInWrapper.childElementCount &&
        !openInWrapper.textContent.trim()
      ) {
        openInWrapper.remove();
      }
    }

    header.querySelectorAll('.copy-btn').forEach((btn) => {
      if (btn.parentElement !== actions) actions.appendChild(btn);
    });

    if (!secret && !actions.querySelector('.open-in')) {
      const openIn = createOpenInMenu(() => resultText(output), toolId);
      openIn.dataset.autoAction = 'open-in';
      actions.appendChild(openIn);
    }

    if (!actions.querySelector('.copy-btn[data-auto-action="copy"], .copy-btn:not([data-auto-action])')) {
      const btn = document.createElement('button');
      btn.className = 'copy-btn';
      btn.dataset.autoAction = 'copy';
      btn.textContent = 'Copy';
      btn.setAttribute('aria-label', 'Copy to clipboard');
      btn.addEventListener('click', () => {
        const text = resultText(output);
        if (!text) return;
        track('copy', { tool: toolId });
        copyToClipboard(text, btn);
      });
      actions.appendChild(btn);
    }

    if (!secret && !actions.querySelector('.copy-btn[data-auto-action="clip"]')) {
      const clipBtn = document.createElement('button');
      clipBtn.className = 'copy-btn';
      clipBtn.dataset.autoAction = 'clip';
      clipBtn.textContent = 'Clip';
      clipBtn.setAttribute('aria-label', 'Save to workspace clipboard');
      clipBtn.addEventListener('click', () => {
        const text = resultText(output);
        if (text) {
          track('clip', { tool: toolId });
          if (captureToWorkspace(text)) {
            clipBtn.textContent = '\u2713';
            setTimeout(() => {
              clipBtn.textContent = 'Clip';
            }, 1500);
          }
        }
      });
      actions.appendChild(clipBtn);
    }

    if (!secret && !actions.querySelector('.copy-btn[data-auto-action="pin"]')) {
      const pinBtn = document.createElement('button');
      pinBtn.className = 'copy-btn';
      pinBtn.dataset.autoAction = 'pin';
      pinBtn.textContent = 'Pin';
      pinBtn.setAttribute('aria-label', 'Pin to investigation notebook');
      pinBtn.addEventListener('click', () => {
        const text = resultText(output);
        if (!text) return;
        const tool = toolById[toolId];
        const inputEl = card.closest('.tool-panel')?.querySelector('.input-field');
        const inputSummary = inputEl ? inputEl.value.trim() : '';
        const result = pinToNotebook({
          toolId,
          toolLabel: tool?.label || toolId,
          content: text,
          inputSummary,
        });
        if (result) {
          track('pin', { tool: toolId });
          pinBtn.textContent = '\u2713';
          setTimeout(() => {
            pinBtn.textContent = 'Pin';
          }, 1500);
          refreshNotebook();
        }
      });
      actions.appendChild(pinBtn);
    }
  });
}

// ─── Lazy init ────────────────────────────────────────────────────────────────

async function ensureInit(toolId) {
  if (initializedTools.has(toolId)) return;

  const tool = toolById[toolId];
  if (!tool) return;

  await tool.load();

  initializedTools.add(toolId);

  const panel = document.querySelector(`.tool-panel[data-tool-panel="${toolId}"]`);
  if (panel) {
    if (tool.primaryInput) panel.dataset.primaryInputSelector = tool.primaryInput;
    else delete panel.dataset.primaryInputSelector;
    panelMap.set(toolId, panel);
    setupCopyButtonsForPanel(panel);
  }
}

/** A short status message at the bottom of the screen (any module can dispatch `unveil:toast`). */
function showToast(message) {
  window.dispatchEvent(new CustomEvent('unveil:toast', { detail: { message } }));
}

function setupToast() {
  const toast = document.createElement('div');
  toast.className = 'app-toast';
  toast.setAttribute('role', 'status');
  toast.setAttribute('aria-live', 'polite');
  document.body.appendChild(toast);

  let hideTimer = null;
  const show = (message) => {
    toast.textContent = message;
    toast.classList.add('is-visible');
    clearTimeout(hideTimer);
    hideTimer = setTimeout(() => {
      toast.classList.remove('is-visible');
    }, 4200);
  };
  window.addEventListener('unveil:toast', (event) => show(event.detail?.message || ''));
  window.addEventListener('unveil:storage-warning', (event) => {
    show(event.detail?.message || 'Local storage is full. New data could not be saved.');
  });
}

// ─── Views ───────────────────────────────────────────────────────────────────

const root = document.documentElement;

function deactivateCurrentTool() {
  if (!activeTool) return;
  autoSave(activeTool);
  fireDeactivate(activeTool);
  activeTool = null;
  setActiveTool(null);
}

// The open tool is saved when the page is hidden or left too — a reload, a closed tab, a phone
// that discards the app in the background — not only when another tool or view is opened
function saveOpenTool() {
  if (activeTool) autoSave(activeTool);
}
window.addEventListener('pagehide', saveOpenTool);
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') saveOpenTool();
});

// ─── URLs ────────────────────────────────────────────────────────────────────
//   /                  portal
//   /code, /occult     enter a side (the URL is replaced by the tool's)
//   /{toolId}          a tool; #s=… carries shared input (utils/share.js)
//   /{pageId}          a page (registry PAGES); #… is a heading on it
// Older #toolId / #code links are redirected to the path form.

function toolPath(toolId) {
  return `/${toolId}`;
}

/** The first path segment: a tool id, a side id, or '' for the portal. */
function routeId() {
  try {
    return decodeURIComponent(window.location.pathname.replace(/^\/+|\/+$/g, ''));
  } catch {
    return window.location.pathname;
  }
}

/** Point the URL at a view; a history entry per visit so Back works. */
function pushPath(path) {
  if (window.location.pathname === path && !window.location.hash) return;
  history.pushState(null, '', path);
}

/** @param {{ keepUrl?: boolean }} options — keepUrl: show the portal without changing the address */
function goPortal({ keepUrl = false } = {}) {
  deactivateCurrentTool();
  closeDrawer({ restoreFocus: false });
  appEl.dataset.view = 'portal';
  setActiveSide(null);
  if (!keepUrl) pushPath('/');
  resetMeta();
  window.scrollTo(0, 0);
}

// ─── Pages ───────────────────────────────────────────────────────────────────
// A page that is not a tool, rendered from data into #page: the top bar and the
// footer, no side menu. A heading's #anchor on it scrolls without re-rendering.

let renderedPage = null;
let pageGeneration = 0;

/** Scroll to the heading the address names, or to the top. */
function scrollToAnchor() {
  let target = null;
  try {
    target = window.location.hash.length > 1 ? document.getElementById(decodeURIComponent(window.location.hash.slice(1))) : null;
  } catch {
    /* a malformed fragment: the top */
  }
  // :target does not follow pushState, so the heading gone to is marked here
  pageEl.querySelector('.is-target')?.classList.remove('is-target');
  if (target && pageEl.contains(target)) {
    target.classList.add('is-target');
    target.scrollIntoView();
  } else window.scrollTo(0, 0);
}

/**
 * @param {{ push?: boolean, hash?: string }} options — push: false when the URL already names the
 * page; hash: a heading on it to open at (#term-ioc)
 */
async function goPage(pageId, { push = true, hash = '' } = {}) {
  const page = pageById[pageId];
  if (!page) return;
  const wasShown = appEl.dataset.view === 'page' && renderedPage === pageId;
  deactivateCurrentTool();
  closeDrawer({ restoreFocus: false });
  appEl.dataset.view = 'page';
  setActiveSide(null);
  if (push) pushPath(`/${pageId}${hash}`);
  setMeta(`${page.label} — Unveil`, page.description, `/${pageId}`);
  if (wasShown) {
    // A heading of the page open already: go to it; its own link (footer): the top; Back and Forward keep the place
    if (hash) scrollToAnchor();
    else if (push) window.scrollTo(0, 0);
    return;
  }
  if (renderedPage !== pageId) {
    const gen = ++pageGeneration;
    let html = null;
    try {
      html = await page.render();
    } catch (error) {
      console.error(`[Unveil] Failed to load the page "${pageId}":`, error); // eslint-disable-line no-console
    }
    if (gen !== pageGeneration || appEl.dataset.view !== 'page') return;
    pageEl.innerHTML = html ?? '<p class="page-text">This page could not be loaded. Check your connection and try again.</p>';
    renderedPage = html === null ? null : pageId;
  }
  scrollToAnchor();
}

const plainClick = (e) => e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;

// Links to a page (footer, tool notes, About) and from a page to a tool stay in the app
document.addEventListener('click', (e) => {
  const link = e.target.closest('a[data-page]');
  if (!link || e.defaultPrevented || !plainClick(e) || !pageById[link.dataset.page]) return;
  e.preventDefault();
  goPage(link.dataset.page, { hash: new URL(link.href).hash });
});

pageEl.addEventListener('click', (e) => {
  const link = e.target.closest('a[data-tool]');
  if (!link || !plainClick(e) || !toolById[link.dataset.tool]) return;
  e.preventDefault();
  // A link that sets the tool to something (the evidence page's claims) carries its state, as a share link does
  if (link.dataset.state) setPendingRestore(JSON.parse(link.dataset.state));
  switchTool(link.dataset.tool);
});

// ─── Sides ───────────────────────────────────────────────────────────────────
// Entering a side opens the tool last used there, or the first tool in its menu.

function lastToolKey(side) {
  return `unveil:last:${side}`;
}

/** @param {'code'|'occult'} side  @param {{ replace?: boolean }} options — replace: the URL already names the side */
function enterSide(side, { replace = false } = {}) {
  if (!roomById[side]) return;
  let toolId = null;
  try {
    toolId = localStorage.getItem(lastToolKey(side));
  } catch {
    /* storage blocked */
  }
  toolId = resolveToolId(toolId);
  if (!toolById[toolId] || roomOfTool(toolId) !== side) toolId = toolsInRoom(side)[0].id;
  if (replace) history.replaceState(null, '', toolPath(toolId));
  switchTool(toolId);
}

document.querySelectorAll('[data-action="home"]').forEach((el) =>
  el.addEventListener('click', (e) => {
    e.preventDefault();
    goPortal();
  }),
);

// ─── Switching ────────────────────────────────────────────────────────────────

// Track the most recent switchTool call so stale callbacks are ignored
let switchGeneration = 0;

/** Saved state under a retired tool's field ids, moved onto the ids of the tool that absorbed it. */
function switchTool(requestedId) {
  // A retired id (a merged tool's old URL, a session or bookmark saved under it) opens its successor
  const toolId = resolveToolId(requestedId);
  const toolEntry = toolById[toolId];
  if (!toolEntry) return;
  const prevTool = activeTool;
  activeTool = toolId;
  const gen = ++switchGeneration;
  const queuedPrefill = consumePrefill(toolId) || consumePrefill(requestedId);
  const alias = TOOL_ALIASES[requestedId];
  const pending = consumePendingRestore();
  const restored = upgradeState(pending?.state ?? null, {
    renamedFields: { ...toolEntry.renamedFields, ...alias?.fields },
    renamedChoices: toolEntry.renamedChoices,
    addedChoices: toolEntry.addedChoices,
  });
  // The retired tool's tab first, so its shared fields are read in it
  const pendingState = alias?.state ? { ...alias.state, ...restored } : restored;

  // Save state & deactivate previous tool
  if (prevTool && prevTool !== toolId) {
    autoSave(prevTool);
    fireDeactivate(prevTool);
  }

  trackRecent(toolId, toolEntry.label);

  const wasTool = appEl.dataset.view === 'tool';
  const side = roomOfTool(toolId);
  appEl.dataset.view = 'tool';
  appEl.dataset.side = side;
  setActiveSide(side);
  setActiveTool(toolId);
  renderToolHead(toolId);
  // A history entry per visit, so Back returns to the previous tool or home
  pushPath(toolPath(toolId));
  updateMeta(toolId);
  try {
    localStorage.setItem('unveil-tool', toolId);
    localStorage.setItem(lastToolKey(side), toolId);
  } catch {
    /* ignored */
  }
  if (!wasTool || prevTool !== toolId) window.scrollTo(0, 0);

  panelMap.forEach((panel) => {
    panel.style.display = 'none';
  });

  // Clean up stale spinners and error divs from previous switches
  panelsContainer.querySelectorAll('.tool-loading, .tool-load-error').forEach((el) => el.remove());

  // Show loading spinner if tool hasn't been initialized yet
  const needsLoad = !initializedTools.has(toolId);
  let spinner = null;
  if (needsLoad) {
    spinner = document.createElement('div');
    spinner.className = 'tool-loading';
    spinner.innerHTML = '<div class="tool-loading-spinner"></div>';
    panelsContainer.appendChild(spinner);
  }

  ensureInit(toolId)
    .then(() => {
      if (gen !== switchGeneration) return;
      if (spinner) spinner.remove();
      panelMap.forEach((panel) => {
        panel.style.display = 'none';
      });
      const panel = panelMap.get(toolId);
      if (panel) {
        panel.style.display = '';
        // Its fields as the tool set them, before anything is restored into them
        if (needsLoad) rememberDefaults(toolId);
        fireActivate(toolId);

        // Restore state: prefill > pending session > autosave
        if (queuedPrefill && toolEntry.handlesPrefill) {
          panel.dispatchEvent(new CustomEvent('unveil:prefill', { detail: queuedPrefill }));
        } else if (queuedPrefill) {
          const text =
            typeof queuedPrefill.text === 'string'
              ? queuedPrefill.text
              : typeof queuedPrefill.date === 'string'
                ? queuedPrefill.date
                : '';
          if (text) applyTextToPrimaryInput(panel, text);
        } else {
          autoRestore(toolId, pendingState || undefined, toolEntry, { whole: pending?.whole });
        }
      }
    })
    .catch((error) => {
      console.error(`[Unveil] Failed to load "${toolId}":`, error); // eslint-disable-line no-console
      if (gen !== switchGeneration) return;
      if (spinner) spinner.remove();
      const err = document.createElement('div');
      err.className = 'tool-load-error';
      err.innerHTML =
        '<p>This tool could not be loaded. Check your connection and try again.</p><button class="tool-mode-btn">Try again</button>';
      err.querySelector('button').addEventListener('click', () => {
        err.remove();
        switchTool(toolId);
      });
      panelsContainer.appendChild(err);
    });
}

// ─── Theme toggle ────────────────────────────────────────────────────────────

const THEME_ICONS = {
  // Shown icon = the theme you switch TO
  light:
    '<svg class="icon" viewBox="0 0 20 20" aria-hidden="true"><circle cx="10" cy="10" r="3.5" /><path d="M10 2.5v2M10 15.5v2M2.5 10h2M15.5 10h2M4.7 4.7l1.4 1.4M13.9 13.9l1.4 1.4M4.7 15.3l1.4-1.4M13.9 6.1l1.4-1.4" /></svg>',
  dark: '<svg class="icon" viewBox="0 0 20 20" aria-hidden="true"><path d="M15.5 12.5A6.5 6.5 0 0 1 7.5 4.5a6.5 6.5 0 1 0 8 8z" /></svg>',
};

function updateThemeButton() {
  const btn = document.getElementById('theme-toggle-btn');
  if (!btn) return;
  const next = root.getAttribute('data-theme') === 'light' ? 'dark' : 'light';
  btn.innerHTML = THEME_ICONS[next];
  const label = next === 'dark' ? 'Switch to dark theme' : 'Switch to light theme';
  btn.title = label;
  btn.setAttribute('aria-label', label);
}

document.getElementById('theme-toggle-btn')?.addEventListener('click', () => {
  const next = root.getAttribute('data-theme') === 'dark' ? 'light' : 'dark';
  root.setAttribute('data-theme', next);
  try {
    localStorage.setItem('unveil:theme', next);
  } catch {
    /* ignored — the choice lasts for this visit */
  }
  updateThemeButton();
});
updateThemeButton();

// Printing is in the light theme, whatever the screen's: a figure a tool colours by script redraws for it
let themeBeforePrint = null;
addEventListener('beforeprint', () => {
  themeBeforePrint = root.getAttribute('data-theme');
  root.setAttribute('data-theme', 'light');
});
addEventListener('afterprint', () => {
  if (themeBeforePrint === null) root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', themeBeforePrint);
});

// ─── Navigation wiring ───────────────────────────────────────────────────────

// Enables openInTool() / openToolWithState() from panel.js (and every cross-tool action) to switch tools.
setNavigator((toolId, { state } = {}) => {
  if (state) setPendingRestore(state);
  switchTool(toolId);
});

initNavigation({
  onSelectTool: switchTool,
  onSelectSide: enterSide,
  onSelectPage: (pageId, hash) => goPage(pageId, { hash }),
  // A search result inside a tool: the tool, opened with the state that shows it (as a share link does)
  onOpenTool: (toolId, state) => {
    setPendingRestore(state);
    switchTool(toolId);
  },
});
initPortal({ onEnterSide: enterSide });

/** Show whatever the URL names: a tool (with shared input), a side, or the portal. */
async function route() {
  const fragment = window.location.hash.slice(1);
  // Older links: /#gematria, /#code
  if (!routeId() && (toolById[fragment] || TOOL_ALIASES[fragment] || roomById[fragment])) {
    history.replaceState(null, '', toolPath(fragment));
  }

  const id = routeId();
  if (toolById[id] || TOOL_ALIASES[id]) {
    const shared = isShareFragment(fragment) ? await decodeShareFragment(fragment) : null;
    // A retired address is replaced by its successor's; switchTool maps the shared fields
    if (fragment || TOOL_ALIASES[id]) history.replaceState(null, '', toolPath(resolveToolId(id)));
    if (shared) setPendingRestore(shared, { whole: true });
    if (shared || resolveToolId(id) !== activeTool) switchTool(id);
  } else if (pageById[id]) {
    goPage(id, { push: false });
  } else if (roomById[id]) {
    enterSide(id, { replace: true });
  } else if (id) {
    // An unknown path shows the portal but keeps the address: a client still running an older
    // build (service worker) reloads into the new one and then finds the tool
    goPortal({ keepUrl: true });
  } else {
    if (fragment) history.replaceState(null, '', '/');
    goPortal();
  }
}

// Back/forward, manual edits of the address
window.addEventListener('popstate', route);

// ─── Share link ──────────────────────────────────────────────────────────────

async function copyShareLink() {
  if (!activeTool) return;
  const base = `${window.location.origin}${toolPath(activeTool)}`;
  const state = captureShareState(activeTool);
  let url = base;
  let message = 'Link to this tool copied';
  if (state) {
    const withState = `${base}#${await encodeShareFragment(state)}`;
    if (withState.length <= MAX_SHARE_URL_LENGTH) {
      url = withState;
      message = 'Link with your input copied';
    } else {
      message = 'Input too long for a link — copied the link to the tool';
    }
  }
  track('share-link', { tool: activeTool, withInput: url !== base });
  try {
    await navigator.clipboard.writeText(url);
    showToast(message);
  } catch {
    showToast('Could not copy — the address bar has the link to this tool');
  }
}

document.getElementById('tool-head')?.addEventListener('click', (e) => {
  if (e.target.closest('[data-action="share"]')) copyShareLink();
});

// ─── Startup ──────────────────────────────────────────────────────────────────

// Installed as an app (PWA)
window.addEventListener('appinstalled', () => track('install'));

route().finally(() => root.removeAttribute('data-booting'));

// ─── Sessions ─────────────────────────────────────────────────────────────────

initNotebook({ closeSessionsFn: closeSessions });

initSessions({
  navigate: switchTool,
  getActiveTool: () => activeTool,
  getActiveToolLabel: () => toolById[activeTool]?.label || activeTool,
  closeNotebookFn: closeNotebook,
});

// ─── About modal ──────────────────────────────────────────────────────────────
import('./about.js').then((m) => m.initAbout());
